import { Resource, Result } from 'src_common/common/mobx-utils/Resource';
import { idle } from 'src_common/utils/Idle';
import * as React from 'react';

const isServer = (): boolean => typeof window === 'undefined';

export type ResourceSSRConfigType<T> = {
    /** An optional label for debugging purposes. */
    label?: string;
    /**
     * If true, the 'loadValue' function will not be executed during Server-Side Rendering (SSR).
     * Instead, the 'defaultValue' will be used.
     * @default false
     */
    disableSSRFetch?: boolean;
    /**
     * The default value to use when 'disableSSRFetch' is true on the server.
     * This must be provided if 'disableSSRFetch' is enabled.
     */
    defaultValue: T;
};

export class ResourceSSR<T> {
    private originalResource: Resource<T>;
    private ssrTimedOut = false;

    public constructor(
        loadValue: () => Promise<T>,
        private readonly config: ResourceSSRConfigType<T>
    ) {
        const disableSSRFetch = this.config.disableSSRFetch ?? false;
        const finalLoadValue =
            isServer() && disableSSRFetch === true ? async (): Promise<T> => config.defaultValue : loadValue;

        this.originalResource = new Resource(finalLoadValue);
    }

    /**
     * Retrieves the current state of the resource.
     *
     * During Server-Side Rendering (SSR), if the resource is still loading,
     * this method will throw a Promise to suspend rendering until the data is fetched
     * or until the `ssrTimeout` is reached. This enables a Suspense-like behavior on the server.
     *
     * On the client-side, if the resource is loading, it registers the ongoing network
     * request with the `idle` service to track application activity.
     *
     * @param options - Optional configuration for the get operation.
     * @param options.ssrTimeout - The maximum time in milliseconds to wait for the data during SSR before timing out. Defaults to 2s.
     * @returns The current result of the resource, which can be 'loading', 'ready', or 'error'.
     * @throws {Promise<void>} A promise that is thrown to suspend server-side rendering while data is loading.
     */
    public get(options?: { ssrTimeout?: number }): Result<T> {
        const result = this.originalResource.get();

        if (result.type === 'loading' && isServer()) {
            if (this.config.disableSSRFetch === true) {
                return result;
            }

            if (this.ssrTimedOut) {
                return result;
            }

            const timeout = options?.ssrTimeout ?? 2000;

            const promiseToThrow = new Promise<void>((resolve) => {
                const timeoutId = setTimeout(() => {
                    console.error(
                        `[ResourceSSRv2: ${this.config.label ?? 'unlabeled'}] SSR Timeout reached after ${timeout}ms. Aborting suspension.`
                    );
                    this.ssrTimedOut = true;
                    resolve();
                }, timeout);

                // Wait not just for the request, but for all subsequent async operations to complete.
                // This prevents premature un-suspending of SSR when a resource's `whenReady`
                // triggers other async work (e.g., in mobx reactions).
                idle.whenIdle(() => {
                    clearTimeout(timeoutId);
                    resolve();
                });
            });

            throw promiseToThrow;
        } else if (result.type === 'loading' && !isServer()) {
            const unsubscribe = idle.registerNetworRequest();

            result.whenReady.finally(() => {
                unsubscribe();
            });
        }

        return result;
    }

    public getReady(options?: { ssrTimeout?: number }): T | null {
        const result = this.get(options);

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    public async refresh(): Promise<void> {
        // Reset timeout state on refresh, so SSR can try blocking again
        this.ssrTimedOut = false;
        await this.originalResource.refresh();
    }
}

export const ssrReactLazy = <T>(
    load: () => Promise<React.ComponentType<T>>
): React.LazyExoticComponent<React.ComponentType<T>> => {
    return React.lazy(async () => {
        const unregister = idle.registerNetworRequest();
        const component = await load();
        unregister();

        return {
            default: component,
        };
    });
};
